import join from 'lodash/fp/join';
import { ErrorResponse, User, UserManager, UserManagerSettings, UserProfile as Profile } from 'oidc-client-ts';

import { config, isTraining, isWorkshopParentFrontend } from '@/config';
import { OAuthConfig } from '@/configuration';
import { AccessToken } from '@/configuration/tokenHandling/tokenSlice';
import { UserProfile } from './loginSlice';
import { mapUserProfile } from './userProfile';

const RETRY_SIGNIN_TIMEOUT_IN_MS = 30000;

const isErrorResponse = (error: unknown): error is ErrorResponse => error instanceof ErrorResponse;

const retrySigninSilent = (oauthConfig: OAuthConfig, userManager: UserManager) => {
    userManager.signinSilent().catch((error: Error) => {
        if (isErrorResponse(error) && error.error === 'login_required') {
            oauthConfig.onSessionExpired();
        } else {
            setTimeout(() => retrySigninSilent(oauthConfig, userManager), RETRY_SIGNIN_TIMEOUT_IN_MS);
        }
    });
};

export interface SessionRenewedResult {
    accessToken: AccessToken;
    idToken: Profile;
    profile: UserProfile;
    locale: string;
}

const adaptPublishedInfo = (result: User): SessionRenewedResult => ({
    accessToken: result.access_token,
    idToken: result.profile,
    locale: result.profile?.locale ?? 'en-GB',
    profile: mapUserProfile(result.profile),
});

export const createUserManager = () => {
    const redirectUri = config.login.redirectUri;
    const silentRedirectUri = config.login.silentRedirectUri;

    const settings: UserManagerSettings = {
        authority: `${config.login.authority}`,
        client_id: `${config.login.clientId}`,
        loadUserInfo: false,
        redirect_uri: `${redirectUri}`,
        response_type: 'code',
        scope: join(' ', config.login.oauthScope),
        silent_redirect_uri: `${silentRedirectUri || redirectUri}`,
        includeIdTokenInSilentRenew: false,
        automaticSilentRenew: true,
        staleStateAgeInSeconds: 600,
        extraQueryParams: { isServiceCareTraining: isTraining() ? 'true' : 'false' },
    };

    return new UserManager(settings);
};

export const configureUserManager = (oauthConfig: OAuthConfig, userManager: UserManager) => {
    userManager.events.addUserLoaded((user) => {
        oauthConfig.onSessionRenewed(adaptPublishedInfo(user as User));
    });

    userManager.events.addUserUnloaded(() => {
        oauthConfig.onSessionExpired();
    });

    userManager.events.addSilentRenewError(() => {
        retrySigninSilent(oauthConfig, userManager);
    });

    userManager.events.addUserSignedOut((...args) => {
        oauthConfig.onSessionExpired();
    });

    return userManager;
};

const base64UrlEncode = (obj: Record<string, unknown>): string => {
    return btoa(JSON.stringify(obj)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const createMockJwt = (payload: Record<string, unknown>): string => {
    const header = { alg: 'RS256', typ: 'JWT', kid: 'mock-key-id' };
    return `${base64UrlEncode(header)}.${base64UrlEncode(payload)}.mock-signature`;
};

// Mock constants (also check LocalJwtTokenService.kt on backend)

// This is the client id defined in frontend
const MOCK_CLIENT_ID = '11111111-1111-1111-1111-111111111111';
const MOCK_USER_FIRST_NAME = 'John';
const MOCK_USER_LAST_NAME = 'Doe';
const MOCK_USER_FULL_NAME = `${MOCK_USER_FIRST_NAME} ${MOCK_USER_LAST_NAME}`;
const MOCK_USER_EMAIL = 'john.doe@digitalhub.man';
const MOCK_SUB = '22222222-2222-2222-2222-222222222222';
const MOCK_AUTH_TIME = Math.floor(Date.now() / 1000) - 1;

// --- RIO ---
const MOCK_ISSUER_RIO = 'http://localhost:8100';
const MOCK_TEST_SUBJECT_ID_READ_WRITE_PERMISSION = 'prod-rio-users:ca605e2c-5c0e-4eaf-bc90-53578e726897';
const MOCK_READ_SCOPE = 'demofleet-player.read';
const MOCK_TEST_ACCOUNT_ID = crypto.randomUUID();

// --- MAN ---
const MOCK_ISSUER_MAN = 'http://localhost-sso:1111';
const MOCK_USER_ID = 'A0001';

const now = () => Math.floor(Date.now() / 1000);

// Token creation (also check LocalJwtTokenService.kt on backend)

const createWorkshopAccessToken = (): string => {
    const claims: Record<string, unknown> = {
        iss: MOCK_ISSUER_MAN,
        exp: now() + 86400,
        nbf: now() - 1,
        aud: [MOCK_CLIENT_ID],
        MFAPolicy: 'false',
        tid: crypto.randomUUID(),
        given_name: MOCK_USER_FIRST_NAME,
        family_name: MOCK_USER_LAST_NAME,
        name: MOCK_USER_FULL_NAME,
        idp: 'MANAADDEV',
        extension_Internal: 'X',
        MANId: MOCK_USER_ID,
        extension_UserEmail: MOCK_USER_EMAIL,
        Organisationstyp: 'W',
        city: 'Muenchen',
        department: 'DEPARTMENT001',
        CompanyInfo: 'MAN Truck & Bus SE',
        streetAddress: 'Dachauer Straße 667',
        postalCode: '80995',
        postalCity: 'München',
        OrgName: 'W00001',
        manCostCenter: 'G2525',
        isServiceCareTraining: isTraining() ? 'true' : 'false',
        salutation: 'Mr.',
        sub: MOCK_SUB,
        userAccess: true,
        azp: MOCK_CLIENT_ID,
        ver: '1.0',
        iat: now() - 3600,
    };
    return createMockJwt(claims);
};

const createWorkshopIdToken = (): Record<string, unknown> => ({
    exp: now() + 86400,
    nbf: now() - 1,
    ver: '1.0',
    iss: MOCK_ISSUER_MAN,
    sub: MOCK_SUB,
    aud: [MOCK_CLIENT_ID],
    acr: 'something-relevant',
    iat: now() - 3600,
    auth_time: MOCK_AUTH_TIME,
    MFAPolicy: 'false',
    tid: crypto.randomUUID(),
    given_name: MOCK_USER_FIRST_NAME,
    family_name: MOCK_USER_LAST_NAME,
    name: MOCK_USER_FULL_NAME,
    idp: 'MANAADDEV',
    extension_Internal: 'X',
    MANId: MOCK_USER_ID,
    extension_UserEmail: MOCK_USER_EMAIL,
    Organisationstyp: 'W',
    city: 'Muenchen',
    department: 'DEPARTMENT001',
    CompanyInfo: 'MAN Truck & Bus SE',
    streetAddress: 'Dachauer Straße 667',
    postalCode: '80995',
    postalCity: 'München',
    OrgName: 'W00001',
    manCostCenter: 'G2525',
    isServiceCareTraining: 'true',
    salutation: 'Mr.',
    userAccess: true,
    azp: MOCK_CLIENT_ID,
    locale: config.login.mockLocale ?? 'en-GB',
    email: MOCK_USER_EMAIL,
});

const createRioAccessToken = (): string => {
    const claims: Record<string, unknown> = {
        sub: MOCK_TEST_SUBJECT_ID_READ_WRITE_PERMISSION,
        scope: MOCK_READ_SCOPE,
        iss: MOCK_ISSUER_RIO,
        account: MOCK_TEST_ACCOUNT_ID,
    };
    return createMockJwt(claims);
};

const createRioIdToken = (): Record<string, unknown> => ({
    sub: MOCK_TEST_SUBJECT_ID_READ_WRITE_PERMISSION,
    scope: MOCK_READ_SCOPE,
    iss: MOCK_ISSUER_RIO,
    aud: [MOCK_CLIENT_ID],
    azp: MOCK_CLIENT_ID,
    iat: now() - 3600,
    exp: now() + 86400,
    account: MOCK_TEST_ACCOUNT_ID,
    tenant: 'rio-eu.test',
    given_name: MOCK_USER_FIRST_NAME,
    family_name: MOCK_USER_LAST_NAME,
    name: MOCK_USER_FULL_NAME,
    username: MOCK_USER_EMAIL,
    locale: config.login.mockLocale ?? 'en-GB',
    email: MOCK_USER_EMAIL,
});

export const configureMockUserManager = (oauthConfig: OAuthConfig): UserManager => {
    console.warn('[configuration/login/oidc-session] Using mocked authorization due to config setting');

    const isWorkshop = isWorkshopParentFrontend();
    const accessToken = isWorkshop ? createWorkshopAccessToken() : createRioAccessToken();
    const profile = isWorkshop ? createWorkshopIdToken() : createRioIdToken();
    const idToken = createMockJwt(profile);

    const signinSilent = () => {
        const userSettings = {
            access_token: accessToken,
            profile: profile as unknown as Profile,
            id_token: idToken,
            session_state: crypto.randomUUID(),
            refresh_token: crypto.randomUUID(),
            token_type: 'bearer',
            scope: 'openid',
            expires_at: now() + 86400,
            state: '',
        };

        const user = new User(userSettings);
        oauthConfig.onSessionRenewed(adaptPublishedInfo(user));
        return Promise.resolve(user);
    };

    const signinRedirect = () => {
        console.warn('[configuration/login/oidc-session] signinRedirect called on mock — returning silently');
        return signinSilent().then(() => {});
    };

    const clearStaleState = () => {
        console.info('[configuration/login/oidc-session] Stale state cleared');
        return Promise.resolve();
    };

    const events = {
        addUserLoaded: () => {},
        addUserUnloaded: () => {},
        addSilentRenewError: () => {},
        addUserSignedOut: () => {},
    };

    return { signinSilent, signinRedirect, clearStaleState, events } as unknown as UserManager;
};
